import { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { toast } from "react-toastify";
import PrescriptionPreviewModal from "./PrescriptionPreviewModal";
import HeaderPreviewModal from "./HeaderPreviewModal";
import AuthContext from "../../../store/auth-context";
import { FaShieldAlt, FaDatabase, FaServer, FaLock, FaUserShield, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaSave, FaCertificate, FaPrescription, FaBuilding, FaCog, FaNotesMedical, FaFileInvoiceDollar, FaLightbulb, FaInfoCircle } from "react-icons/fa";
import { Spinner } from "react-bootstrap";
import CertificateManagement from "./CertificateManagement";
import PrescriptionManagement from "./PrescriptionManagement";
import DiagnosisManagement from "./DiagnosisManagement";
import AdviceManagement from "./AdviceManagement";
import InstructionsManagement from "./InstructionsManagement";

const Config = () => {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const { t } = useTranslation();
  console.log(authCtx, "from config");
  const [activeTab, setActiveTab] = useState("hospitalInfo"); // 'hospitalInfo' or 'systemConfig'

  const [localHospitalInfo, setLocalHospitalInfo] = useState({
    hId: 0,
    name: "",
    address: "",
    city: "",
    tahsil: "",
    district: "",
    state: "",
    country: "",
    phoneNumber: "",
    altPhoneNumber: "",
    email: "",
    website: "",
    description: "",
    speciality: "",
    registrationNumber: ""
  });

  const [configurations, setConfigurations] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showHeaderPreview, setShowHeaderPreview] = useState(false);
  const [selectedTemplateForPreview, setSelectedTemplateForPreview] = useState("1");
  const [selectedHeaderForPreview, setSelectedHeaderForPreview] = useState("1");
  const [isSaving, setIsSaving] = useState(false);

  // Sync local state with context when context updates
  useEffect(() => {
    if (authCtx.hospitalInfo && Object.keys(authCtx.hospitalInfo).length > 0) {
      setLocalHospitalInfo((prev) => ({
        ...prev,
        ...authCtx.hospitalInfo
      }));
    }
  }, [authCtx.hospitalInfo]);

  // Fetch System Configurations on mount
  useEffect(() => {
    const fetchConfig = async () => {
      const token = getAuthToken();
      if (!token || token === "EXPIRED") {
        navigate("/");
        return;
      }

      setLoadingConfig(true);
      try {
        const apiChar = getApiChara();
        const response = await fetch(`${apiChar}/HospitalConfiguration/getHospitalInfoWithAllConfigDetails`, {
          headers: { Authorization: "Bearer " + token },
        });

        if (!response.ok) throw new Error("Could not fetch configuration data");

        const data = await response.json();
        let configs = data.configurations || [];
        // Ensure selectHeader exists
        if (!configs.find(c => c.paramCode === "selectHeader")) {
          configs.push({
            paramCode: "selectHeader",
            paramValue: "1",
            paramName: "Select Header Design"
          });
        }
        if (!configs.find(c => c.paramCode === "applicationThemeColour")) {
          configs.push({
            paramCode: "applicationThemeColour",
            paramValue: "TEAL",
            paramName: "Application Theme Colour"
          });
        }
        if (!configs.find(c => c.paramCode === "applicationLanguage")) {
          configs.push({
            paramCode: "applicationLanguage",
            paramValue: "ENGLISH",
            paramName: "Application Language"
          });
        }
        if (!configs.find(c => c.paramCode === "boolPrintCasePaper")) {
          configs.push({
            paramCode: "boolPrintCasePaper",
            paramValue: "false",
            paramName: "Print Case Paper"
          });
        }
        const savedBgColor = localStorage.getItem("temp_headerBgColor");
        let bgColorConfig = configs.find(c => c.paramCode === "headerBgColor");
        if (bgColorConfig) {
          if (savedBgColor) bgColorConfig.paramValue = savedBgColor;
        } else {
          configs.push({
            paramCode: "headerBgColor",
            paramValue: savedBgColor || "#f0fdfa",
            paramName: "Header Background Colour"
          });
        }
        setConfigurations(configs);
      } catch (error) {
        console.error(error);
        toast.error(t("Error fetching system configuration"));
      } finally {
        setLoadingConfig(false);
      }
    };

    fetchConfig();
  }, [navigate]);

  /* ---------------- Hospital Info Handlers ---------------- */
  const handleHospitalInfoChange = (e) => {
    const { name, value } = e.target;
    setLocalHospitalInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateHospitalInfo = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const token = getAuthToken();
    const apiChar = getApiChara();

    try {
      const response = await fetch(`${apiChar}/hospitalInfo/update/${localHospitalInfo.hId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(localHospitalInfo),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to update hospital info");
      }

      toast.success(t("Hospital Information updated successfully!"));
      authCtx.fetchHospitalData();
    } catch (error) {
      console.error(error);
      toast.error(t("Error updating hospital info: ") + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  /* ---------------- System Config Handlers ---------------- */
  const handleConfigChange = (index, value) => {
    const updatedConfigs = configurations.map((config, i) =>
      i === index ? { ...config, paramValue: value } : config
    );
    setConfigurations(updatedConfigs);

    // Persist to localStorage if it's the header background color
    const changedConfig = updatedConfigs[index];
    if (changedConfig && changedConfig.paramCode === "headerBgColor") {
      localStorage.setItem("temp_headerBgColor", value);
    }
  };

  const handleUpdateSystemConfig = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const token = getAuthToken();
    const apiChar = getApiChara();

    try {
      const response = await fetch(`${apiChar}/HospitalConfiguration/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(configurations),
      });

      if (!response.ok) throw new Error("Failed to update system configuration");

      toast.success(t("System Configuration updated successfully!"));
      localStorage.removeItem("temp_headerBgColor");

      const configMap = {};
      configurations.forEach(c => configMap[c.paramCode] = c.paramValue);
      authCtx.setHospitalData(authCtx.hospitalInfo, configMap);

    } catch (error) {
      console.error(error);
      toast.error(t("Error updating system configuration"));
    } finally {
      setIsSaving(false);
    }
  };

  const labelStyle = {
    display: "block",
    marginBottom: "5px",
    fontWeight: "600",
    color: "#495057",
    fontSize: "0.85rem",
  };

  const inputStyle = {
    borderRadius: "4px",
    border: "1px solid #ced4da",
    padding: "6px 8px",
    fontSize: "0.9rem",
    width: "100%",
    transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
  };

  const cardStyle = {
    background: "#ffffff",
    padding: "20px",
    borderRadius: "10px",
    boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
    border: "1px solid #e9ecef",
  };

  const tabStyle = (isActive) => ({
    padding: window.innerWidth <= 768 ? "8px 5px" : "10px 20px",
    border: "none",
    background: "transparent",
    borderBottom: isActive ? "3px solid var(--primary-color)" : "3px solid transparent",
    color: isActive ? "var(--primary-color)" : "#6c757d",
    fontWeight: isActive ? "700" : "600",
    cursor: "pointer",
    fontSize: window.innerWidth <= 768 ? "0.68rem" : "0.95rem",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: window.innerWidth <= 768 ? "column" : "row",
    alignItems: "center",
    justifyContent: "center",
    flex: window.innerWidth <= 768 ? "1 0 18%" : "none",
    gap: window.innerWidth <= 768 ? "4px" : "8px",
    textAlign: "center",
    minWidth: 0,
    lineHeight: "1.2"
  });

  return (
    <div style={{ padding: "15px", background: "#f8f9fa", minHeight: "100vh" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>

        {/* Page Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
          <h3 style={{ margin: 0, color: "#343a40", fontWeight: "700", letterSpacing: "-0.5px", fontSize: "1.5rem" }}>{t("Configuration")}</h3>
          <button
            className="btn btn-outline-secondary btn-sm px-3"
            onClick={() => navigate(-1)}
            style={{ borderRadius: "20px", fontWeight: "600", fontSize: "0.85rem" }}
          >
            &larr; {t("Back")}
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: "flex",
          gap: window.innerWidth <= 768 ? "2px" : "10px",
          marginBottom: "15px",
          borderBottom: "1px solid #e9ecef",
          flexWrap: "wrap",
          paddingBottom: "5px",
          justifyContent: window.innerWidth <= 768 ? "center" : "flex-start"
        }}>
          <button
            style={tabStyle(activeTab === "hospitalInfo")}
            onClick={() => setActiveTab("hospitalInfo")}
          >
            <FaBuilding className={window.innerWidth <= 768 ? "mb-0" : "me-2"} />
            <span>{window.innerWidth <= 768 ? t("Hospital") : t("Hospital")}</span>
          </button>
          <button
            style={tabStyle(activeTab === "systemConfig")}
            onClick={() => setActiveTab("systemConfig")}
          >
            <FaCog className={window.innerWidth <= 768 ? "mb-0" : "me-2"} />
            <span>{window.innerWidth <= 768 ? t("System") : t("System")}</span>
          </button>
          <button
            style={tabStyle(activeTab === "privacyPolicy")}
            onClick={() => setActiveTab("privacyPolicy")}
          >
            <FaShieldAlt className={window.innerWidth <= 768 ? "mb-0" : "me-2"} />
            <span>{window.innerWidth <= 768 ? t("Privacy") : t("Privacy Policy")}</span>
          </button>
          <button
            style={tabStyle(activeTab === "certificateManagement")}
            onClick={() => setActiveTab("certificateManagement")}
          >
            <FaCertificate className={window.innerWidth <= 768 ? "mb-0" : "me-2"} />
            <span>{window.innerWidth <= 768 ? t("Certificate") : t("Certificate")}</span>
          </button>
          <button
            style={tabStyle(activeTab === "prescriptionManagement")}
            onClick={() => setActiveTab("prescriptionManagement")}
          >
            <FaPrescription className={window.innerWidth <= 768 ? "mb-0" : "me-2"} />
            <span>{window.innerWidth <= 768 ? t("Prescription") : t("Prescription")}</span>
          </button>
          <button
            style={tabStyle(activeTab === "diagnosisManagement")}
            onClick={() => setActiveTab("diagnosisManagement")}
          >
            <FaNotesMedical className={window.innerWidth <= 768 ? "mb-0" : "me-2"} />
            <span>{window.innerWidth <= 768 ? t("Diagnosis") : t("Diagnosis")}</span>
          </button>
          <button
            style={tabStyle(activeTab === "adviceManagement")}
            onClick={() => setActiveTab("adviceManagement")}
          >
            <FaLightbulb className={window.innerWidth <= 768 ? "mb-0" : "me-2"} />
            <span>{window.innerWidth <= 768 ? t("Advice") : t("Advice")}</span>
          </button>
          <button
            style={tabStyle(activeTab === "instructionsManagement")}
            onClick={() => setActiveTab("instructionsManagement")}
          >
            <FaInfoCircle className={window.innerWidth <= 768 ? "mb-0" : "me-2"} />
            <span>{window.innerWidth <= 768 ? t("Instructions") : t("Instructions")}</span>
          </button>
        </div>

        {/* Dynamic Content Area */}
        <div style={cardStyle}>

          {/* Section 1: Hospital Info */}
          {activeTab === "hospitalInfo" && (
            <form onSubmit={handleUpdateHospitalInfo}>
              <div style={{ display: "grid", gridTemplateColumns: window.innerWidth <= 768 ? "1fr" : "repeat(auto-fit, minmax(220px, 1fr))", gap: "15px" }}>
                {/* Name and Basic Details */}
                {[
                  { key: "name", label: "Hospital Name" },
                  { key: "registrationNumber", label: "Registration No." },
                  { key: "email", label: "Email Address" },
                  { key: "website", label: "Website" },
                  { key: "phoneNumber", label: "Phone Number" },
                  { key: "altPhoneNumber", label: "Alt Phone Number" },
                  { key: "speciality", label: "Speciality" },
                ].map((field) => (
                  <div key={field.key}>
                    <label style={labelStyle}>{t(field.label)}</label>
                    <input
                      type={field.key === "email" ? "email" : "text"}
                      name={field.key}
                      className="form-control"
                      style={inputStyle}
                      value={localHospitalInfo[field.key] || ""}
                      onChange={handleHospitalInfoChange}
                      placeholder={t(field.label)}
                    />
                  </div>
                ))}

                {/* Location Details */}
                {[
                  { key: "city", label: "City" },
                  { key: "tahsil", label: "Tahsil" },
                  { key: "district", label: "District" },
                  { key: "state", label: "State" },
                  { key: "country", label: "Country" },
                ].map((field) => (
                  <div key={field.key}>
                    <label style={labelStyle}>{t(field.label)}</label>
                    <input
                      type="text"
                      name={field.key}
                      className="form-control"
                      style={inputStyle}
                      value={localHospitalInfo[field.key] || ""}
                      onChange={handleHospitalInfoChange}
                      placeholder={t(field.label)}
                    />
                  </div>
                ))}

                {/* Address & Description */}
                <div style={{ gridColumn: window.innerWidth <= 768 ? "span 1" : "span 2" }}>
                  <label style={labelStyle}>{t("Address")}</label>
                  <textarea
                    name="address"
                    className="form-control"
                    style={{ ...inputStyle, minHeight: "60px" }}
                    rows={2}
                    value={localHospitalInfo.address || ""}
                    onChange={handleHospitalInfoChange}
                    placeholder={t("Full Address")}
                  />
                </div>
                <div style={{ gridColumn: window.innerWidth <= 768 ? "span 1" : "span 2" }}>
                  <label style={labelStyle}>{t("Description")}</label>
                  <textarea
                    name="description"
                    className="form-control"
                    style={{ ...inputStyle, minHeight: "60px" }}
                    rows={2}
                    value={localHospitalInfo.description || ""}
                    onChange={handleHospitalInfoChange}
                    placeholder={t("Brief description...")}
                  />
                </div>
              </div>

              <div style={{ marginTop: "20px", textAlign: "right", borderTop: "1px solid #f0f0f0", paddingTop: "15px" }}>
                <button type="submit" className="btn btn-premium btn-sm px-4 d-inline-flex align-items-center" disabled={isSaving}>
                  {isSaving ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
                  {isSaving ? t("Saving...") : t("Save Hospital Info")}
                </button>
              </div>
            </form>
          )}

          {activeTab === "systemConfig" && (
            <form onSubmit={handleUpdateSystemConfig}>

              {loadingConfig ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-info" role="status">
                    <span className="visually-hidden">{t("Loading...")}</span>
                  </div>
                  <p className="mt-2 text-muted">{t("Loading settings...")}</p>
                </div>
              ) : (
                <>
                  {/* Compact Switch Grid */}
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: window.innerWidth <= 768 ? "1fr 1fr" : "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "10px",
                    marginBottom: "20px"
                  }}>
                    {configurations
                      .filter(c => c.paramValue === "true" || c.paramValue === "false" || c.paramCode.toLowerCase().startsWith("bool"))
                      .map((config, index) => (
                        <div
                          key={config.id || index}
                          style={{
                            border: "1px solid #e9ecef",
                            borderRadius: "6px",
                            padding: "8px 10px",
                            background: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            minWidth: 0 // Prevent overflow in grid
                          }}
                        >
                          <label className="form-check-label mb-0" htmlFor={`switch-${index}`} style={{
                            ...labelStyle,
                            margin: 0,
                            fontSize: window.innerWidth <= 768 ? "0.75rem" : "0.85rem",
                            cursor: "pointer",
                            marginRight: "5px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}>
                            {t(config.paramName || config.paramCode)}
                          </label>
                          <div className="form-check form-switch m-0" style={{ minHeight: "auto", paddingLeft: "1.5rem" }}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`switch-${index}`}
                              checked={config.paramValue === "true"}
                              onChange={(e) =>
                                handleConfigChange(
                                  configurations.indexOf(config), // get original index
                                  e.target.checked ? "true" : "false"
                                )
                              }
                              style={{ width: "2rem", height: "1rem", cursor: "pointer", marginTop: "0" }}
                            />
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* Text/Select Configs rearranged into rows */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

                    {/* Row: Opd Fee, Prescription Footer, Set Prescription Header */}
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: window.innerWidth <= 768 ? "1fr" : "1fr 1.5fr 1.5fr",
                      gap: "15px"
                    }}>
                      {/* Opd Fee */}
                      {configurations.find(c => c.paramCode === "opdFee") && (() => {
                        const config = configurations.find(c => c.paramCode === "opdFee");
                        return (
                          <div>
                            <label style={labelStyle}>{t(config.paramName || "Opd Fee")}</label>
                            <textarea
                              className="form-control"
                              style={inputStyle}
                              value={config.paramValue || ""}
                              onChange={(e) => handleConfigChange(configurations.indexOf(config), e.target.value)}
                              rows={2}
                            />
                          </div>
                        );
                      })()}

                      {/* Prescription Footer */}
                      {configurations.find(c => c.paramCode === "prescriptionFooter" || c.paramName === "Set Prescription Footer") && (() => {
                        const config = configurations.find(c => c.paramCode === "prescriptionFooter" || c.paramName === "Set Prescription Footer");
                        return (
                          <div>
                            <label style={labelStyle}>{t(config.paramName || " Set Prescription Footer")}</label>
                            <textarea
                              className="form-control"
                              style={inputStyle}
                              value={config.paramValue || ""}
                              onChange={(e) => handleConfigChange(configurations.indexOf(config), e.target.value)}
                              rows={2}
                            />
                          </div>
                        );
                      })()}

                      {/* Set Prescription Header */}
                      {configurations.find(c => c.paramCode === "prescriptionHeader" || c.paramName === "Set Prescription Header") && (() => {
                        const config = configurations.find(c => c.paramCode === "prescriptionHeader" || c.paramName === "Set Prescription Header");
                        return (
                          <div>
                            <label style={labelStyle}>{t(config.paramName || "Set Prescription Header")}</label>
                            <textarea
                              className="form-control"
                              style={inputStyle}
                              value={config.paramValue || ""}
                              onChange={(e) => handleConfigChange(configurations.indexOf(config), e.target.value)}
                              rows={2}
                            />
                          </div>
                        );
                      })()}
                    </div>

                    {/* Row: Prescription Language, Select Prescription Design */}
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: window.innerWidth <= 768 ? "1fr" : "1fr 1fr 1fr",
                      gap: "15px"
                    }}>
                      {/* Prescription Language Dropdown linked to selectLanguage config */}
                      {configurations.find(c => c.paramCode === "selectLanguage") ? (() => {
                        const config = configurations.find(c => c.paramCode === "selectLanguage");
                        return (
                          <div>
                            <label style={labelStyle}>{t("Prescription Language")}</label>
                            <select
                              className="form-select"
                              style={inputStyle}
                              value={config.paramValue?.toUpperCase() || "ENGLISH"}
                              onChange={(e) => handleConfigChange(configurations.indexOf(config), e.target.value)}
                            >
                              <option value="ENGLISH">{t("English")}</option>
                              <option value="MARATHI">{t("Marathi")}</option>
                              <option value="HINDI">{t("Hindi")}</option>
                            </select>
                          </div>
                        );
                      })() : (
                        <div>
                          <label style={labelStyle}>{t("Prescription Language")}</label>
                          <div className="text-muted small">Language setting not found.</div>
                        </div>
                      )}

                      {/* Select Prescription Design */}
                      {configurations.find(c => c.paramCode === "selectPrescription") && (() => {
                        const config = configurations.find(c => c.paramCode === "selectPrescription");
                        return (
                          <div>
                            <label style={labelStyle}>{t(config.paramName || "Select Prescription Design")}</label>
                            <select
                              className="form-select"
                              style={inputStyle}
                              value={config.paramValue}
                              onChange={(e) => handleConfigChange(configurations.indexOf(config), e.target.value)}
                            >
                              <option value="">-- {t("Select")} --</option>
                              <option value="1">{t("Template 1")}</option>
                              <option value="2">{t("Template 2")}</option>
                              <option value="3">{t("Template 3")}</option>
                            </select>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-theme mt-2"
                              onClick={() => {
                                setSelectedTemplateForPreview(config.paramValue || "1");
                                setShowPreview(true);
                              }}
                            >
                              {t("Preview Full Design")}
                            </button>
                          </div>
                        );
                      })()}

                      {/* Select Header Template */}
                      {configurations.find(c => c.paramCode === "selectHeader") && (() => {
                        const config = configurations.find(c => c.paramCode === "selectHeader");
                        return (
                          <div>
                            <label style={labelStyle}>{t("Select Header Template")}</label>
                            <select
                              className="form-select"
                              style={inputStyle}
                              value={config.paramValue}
                              onChange={(e) => handleConfigChange(configurations.indexOf(config), e.target.value)}
                            >
                              <option value="1">{t("Header Template 1")}</option>
                              <option value="2">{t("Header Template 2")}</option>
                              <option value="3">{t("Header Template 3")}</option>
                              <option value="4">{t("Header Template 4 (Logo)")}</option>
                            </select>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-theme mt-2"
                              onClick={() => {
                                setSelectedHeaderForPreview(config.paramValue || "1");
                                setShowHeaderPreview(true);
                              }}
                            >
                              {t("Preview Header")}
                            </button>
                          </div>
                        );
                      })()}

                      {/* Application Theme Colour */}
                      {configurations.find(c => c.paramCode === "applicationThemeColour") && (() => {
                        const config = configurations.find(c => c.paramCode === "applicationThemeColour");
                        return (
                          <div>
                            <label style={labelStyle}>{t("Application Theme Colour")}</label>
                            <select
                              className="form-select"
                              style={inputStyle}
                              value={config.paramValue}
                              onChange={(e) => handleConfigChange(configurations.indexOf(config), e.target.value)}
                            >
                              <option value="TEAL">{t("Teal (Premium)")}</option>
                              <option value="BLUE">{t("Classic Blue")}</option>
                              <option value="GREEN">{t("Natural Green")}</option>
                              <option value="RED">{t("Bold Red")}</option>
                              <option value="PURPLE">{t("Royal Purple")}</option>
                              <option value="INDIGO">{t("Vibrant Indigo")}</option>
                              <option value="ORANGE">{t("Warm Orange")}</option>
                              <option value="NAVY">{t("Professional Navy")}</option>
                            </select>
                          </div>
                        );
                      })()}

                      {/* Application Language Dropdown */}
                      {configurations.find(c => c.paramCode === "applicationLanguage") && (() => {
                        const config = configurations.find(c => c.paramCode === "applicationLanguage");
                        return (
                          <div>
                            <label style={labelStyle}>{t("Application Language")}</label>
                            <select
                              className="form-select"
                              style={inputStyle}
                              value={config.paramValue?.toUpperCase() || "ENGLISH"}
                              onChange={(e) => handleConfigChange(configurations.indexOf(config), e.target.value)}
                            >
                              <option value="ENGLISH">{t("English")}</option>
                              <option value="MARATHI">{t("Marathi")}</option>
                              <option value="HINDI">{t("Hindi")}</option>
                            </select>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Select Specialities */}
                    {configurations.find(c => c.paramCode === "selectSpecialities") && (() => {
                      const config = configurations.find(c => c.paramCode === "selectSpecialities");
                      return (
                        <div>
                          <label style={labelStyle}>{t(config.paramName || "Select Specialities")}</label>
                          <input
                            type="text"
                            className="form-control"
                            style={{ ...inputStyle, backgroundColor: "#e9ecef", cursor: "not-allowed" }}
                            value={config.paramValue || ""}
                            disabled={true}
                          />
                        </div>
                      );
                    })()}

                    {/* Any other remaining configs that aren't the ones we manually placed */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px" }}>
                      {configurations
                        .filter(c =>
                          !(c.paramValue === "true" || c.paramValue === "false" || c.paramCode.toLowerCase().startsWith("bool")) &&
                          !["opdFee", "prescriptionFooter", "prescriptionHeader", "selectPrescription", "selectHeader", "selectLanguage", "PrescFooter", "PrescHeader", "applicationThemeColour", "applicationLanguage", "selectSpecialities"].includes(c.paramCode)
                        )
                        .map((config) => {
                          const originalIndex = configurations.indexOf(config);
                          return (
                            <div key={config.id}>
                              <label style={labelStyle}>{t(config.paramName || config.paramCode)}</label>
                              <textarea
                                className="form-control"
                                style={inputStyle}
                                value={config.paramValue || ""}
                                onChange={(e) => handleConfigChange(originalIndex, e.target.value)}
                                rows={2}
                              />
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  <div style={{ marginTop: "20px", textAlign: "right", borderTop: "1px solid #f0f0f0", paddingTop: "15px" }}>
                    <button type="submit" className="btn btn-premium btn-sm px-4 d-inline-flex align-items-center" disabled={isSaving}>
                      {isSaving ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
                      {isSaving ? t("Saving...") : t("Save System Config")}
                    </button>
                  </div>
                </>
              )}
            </form>
          )}

          {/* Section 6: Diagnosis Management */}
          {activeTab === "diagnosisManagement" && <DiagnosisManagement />}


          {/* Section 3: Privacy Policy */}
          {activeTab === "privacyPolicy" && (
            <div style={{ color: "#495057", lineHeight: "1.7" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid var(--primary-color)", paddingBottom: "10px", marginBottom: "20px" }}>
                <div>
                  <h4 style={{ color: "var(--primary-color)", margin: 0, fontWeight: "700" }}>{t("Privacy Policy")}</h4>
                  <p style={{ fontSize: "0.75rem", color: "#6c757d", margin: 0 }}>{t("Effective Date")}: February 12, 2026 | {t("Last Updated")}: February 12, 2026</p>
                </div>
                <FaShieldAlt style={{ fontSize: "2rem", color: "var(--primary-color)", opacity: 0.8 }} />
              </div>

              <div style={{ maxHeight: "65vh", overflowY: "auto", paddingRight: "15px", fontSize: "0.85rem" }}>

                <section style={{ marginBottom: "25px", background: "#f8fdfd", padding: "15px", borderRadius: "8px", borderLeft: "4px solid var(--primary-color)" }}>
                  <h5 style={{ fontWeight: "700", color: "var(--primary-color)", display: "flex", alignItems: "center", gap: "10px", margin: "0 0 10px 0" }}>
                    <span style={{ background: "var(--primary-color)", color: "white", borderRadius: "50%", width: "24px", height: "24px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem" }}>1</span>
                    {t("Introduction")}
                  </h5>
                  <p style={{ margin: 0 }}>
                    {t("Welcome to Dr Tech Buddy, a Hospital Management System developed and operated by Invensoft Solutions. We are committed to protecting the privacy and security of the data processed through our platform.")}
                    <br />
                    {t("This policy outlines how we handle personal data in compliance with India’s Digital Personal Data Protection (DPDP) Act, 2023 and the DPDP Rules, 2025.")}
                  </p>
                </section>

                <section style={{ marginBottom: "25px" }}>
                  <h5 style={{ fontWeight: "700", borderBottom: "1px solid #eee", paddingBottom: "8px", marginBottom: "12px", color: "#343a40" }}>
                    2. {t("Our Roles Under the DPDP Act")}
                  </h5>
                  <p style={{ color: "#6c757d", fontSize: "0.8rem", fontStyle: "italic", marginBottom: "12px" }}>{t("To ensure clarity on legal liability, we distinguish between two types of data:")}</p>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "15px" }}>
                    <div style={{ background: "#fff", border: "1px solid #eef2f2", padding: "15px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
                      <h6 style={{ fontWeight: "700", color: "var(--primary-color)", marginBottom: "8px" }}>A. {t("Client Data")}</h6>
                      <p style={{ fontSize: "0.75rem", marginBottom: "8px", color: "#6c757d" }}><em>({t("You are the Data Fiduciary")})</em></p>
                      <ul style={{ paddingLeft: "18px", margin: 0, fontSize: "0.85rem" }}>
                        <li><strong>{t("Collected")}:</strong> {t("Name")}, {t("Email")}, {t("Phone")}, {t("Hospital Information")}.</li>
                        <li><strong>{t("Purpose")}:</strong> {t("Account management, billing, and technical support.")}</li>
                      </ul>
                    </div>
                    <div style={{ background: "#fff", border: "1px solid #eef2f2", padding: "15px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
                      <h6 style={{ fontWeight: "700", color: "#cc0000", marginBottom: "8px" }}>B. {t("Patient Data")}</h6>
                      <p style={{ fontSize: "0.75rem", marginBottom: "8px", color: "#6c757d" }}><em>({t("You are the Data Processor")})</em></p>
                      <ul style={{ paddingLeft: "18px", margin: 0, fontSize: "0.85rem" }}>
                        <li><strong>{t("Processed")}:</strong> {t("Patient names, health records, medical history, and treatments.")}</li>
                        <li><strong>{t("Responsibility")}:</strong> {t("The Hospital is solely responsible for getting patient consent.")}</li>
                      </ul>
                    </div>
                  </div>
                </section>

                <section style={{ marginBottom: "25px" }}>
                  <h5 style={{ fontWeight: "700", borderBottom: "1px solid #eee", paddingBottom: "8px", marginBottom: "12px" }}>3. {t("How We Store & Secure Data")}</h5>
                  <p style={{ fontSize: "0.85rem", marginBottom: "15px" }}>{t("We employ a 3 tier architecture with strict data isolation to ensure maximum security.")}</p>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "15px" }}>
                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                      <FaDatabase style={{ color: "var(--primary-color)", fontSize: "1.1rem", marginTop: "3px" }} />
                      <div>
                        <strong style={{ display: "block", fontSize: "0.85rem" }}>{t("Database Isolation")}</strong>
                        <span style={{ fontSize: "0.8rem", color: "#6c757d" }}>{t("Every hospital is assigned a separate, isolated database.")}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                      <FaServer style={{ color: "var(--primary-color)", fontSize: "1.1rem", marginTop: "3px" }} />
                      <div>
                        <strong style={{ display: "block", fontSize: "0.85rem" }}>{t("Server Location")}</strong>
                        <span style={{ fontSize: "0.8rem", color: "#6c757d" }}>{t("All data is hosted on secure Cloud Platform, located within India. We do not transfer health data across borders.")}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                      <FaLock style={{ color: "var(--primary-color)", fontSize: "1.1rem", marginTop: "3px" }} />
                      <div>
                        <strong style={{ display: "block", fontSize: "0.85rem" }}>{t("Encryption")}</strong>
                        <span style={{ fontSize: "0.8rem", color: "#6c757d" }}>{t("Data is encrypted in transit (SSL/TLS) and at rest where applicable.")}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                      <FaUserShield style={{ color: "var(--primary-color)", fontSize: "1.1rem", marginTop: "3px" }} />
                      <div>
                        <strong style={{ display: "block", fontSize: "0.85rem" }}>{t("Access Control")}</strong>
                        <span style={{ fontSize: "0.8rem", color: "#6c757d" }}>{t("Only authorized personnel with valid credentials can access the system. We maintain logs of all access for security audits.")}</span>
                      </div>
                    </div>
                  </div>
                </section>

                <section style={{ marginBottom: "25px" }}>
                  <h5 style={{ fontWeight: "700", borderBottom: "1px solid #eee", paddingBottom: "8px", marginBottom: "12px" }}>4. {t("Data Retention and Deletion")}</h5>
                  <div style={{ background: "#fafafa", padding: "15px", borderRadius: "8px", border: "1px solid #eee" }}>
                    <p style={{ marginBottom: "10px" }}><strong>{t("Client Data")}:</strong> {t("Retained as long as subscription is active and per Indian tax laws (financial records).")}</p>
                    <p style={{ margin: 0 }}><strong>{t("Patient Data")}:</strong> {t("Retained only during subscription. Upon termination:")}</p>
                    <ol style={{ marginTop: "8px", marginBottom: 0, paddingLeft: "20px" }}>
                      <li>{t("We provide a full downloadable export of the Hospital's data.")}</li>
                      <li>{t("We permanently delete the Hospital’s separate database from our Cloud within 60 days of contract termination.")}</li>
                    </ol>
                  </div>
                </section>

                <section style={{ marginBottom: "25px" }}>
                  <h5 style={{ fontWeight: "700", borderBottom: "1px solid #eee", paddingBottom: "8px", marginBottom: "12px" }}>5. {t("Your Rights (Data Principal)")}</h5>
                  <ul style={{ marginTop: "8px", lineHeight: "1.8", paddingLeft: "20px" }}>
                    <li>{t("Access the personal data we hold about you (e.g., your profile).")}</li>
                    <li>{t("Request correction of inaccurate details.")}</li>
                    <li>{t("Withdraw consent for marketing communications.")}</li>
                  </ul>
                </section>

                <section style={{ marginBottom: "25px" }}>
                  <h5 style={{ fontWeight: "700", borderBottom: "1px solid #eee", paddingBottom: "8px", marginBottom: "12px" }}>6. {t("Disclosure to Third Parties")}</h5>
                  <p>{t("We do not sell or trade personal data. We may share data only with Legal Authorities if required by Indian law or a court order.")}</p>
                </section>

                <section style={{ marginBottom: "10px" }}>
                  <h5 style={{ fontWeight: "700", borderBottom: "1px solid #eee", paddingBottom: "8px", marginBottom: "12px" }}>7. {t("Grievance Redressal")}</h5>
                  <p style={{ fontSize: "0.85rem", marginBottom: "15px" }}>{t("If you have any questions or grievances, contact our Data Protection Officer (DPO):")}</p>
                  <div style={{ background: "#f1f6f6", padding: "20px", borderRadius: "10px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <FaShieldAlt style={{ color: "var(--primary-color)" }} />
                      <span><strong>Invensoft Solutions</strong></span>
                    </div>
                    <a href="mailto:ajay.nigade19@gmail.com" style={{ display: "flex", alignItems: "center", gap: "10px", color: "inherit", textDecoration: "none" }}>
                      <FaEnvelope style={{ color: "var(--primary-color)" }} />
                      <span style={{ fontSize: "0.85rem" }}>ajay.nigade19@gmail.com</span>
                    </a>
                    <a href="tel:7559383489" style={{ display: "flex", alignItems: "center", gap: "10px", color: "inherit", textDecoration: "none" }}>
                      <FaPhoneAlt style={{ color: "var(--primary-color)" }} />
                      <span>7559383489</span>
                    </a>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <FaMapMarkerAlt style={{ color: "var(--primary-color)" }} />
                      <span style={{ fontSize: "0.8rem" }}>Katraj, Pune, Maharashtra</span>
                    </div>
                  </div>
                  <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
                    <a href="https://wa.me/917559383489" target="_blank" rel="noopener noreferrer" className="btn btn-premium btn-sm">
                      {t("Chat on WhatsApp")}
                    </a>
                    <a href="tel:7559383489" className="btn btn-secondary-premium btn-sm">
                      {t("Call Us")}
                    </a>
                    <a href="mailto:ajay.nigade19@gmail.com" className="btn btn-secondary-premium btn-sm">
                      {t("Email Us")}
                    </a>
                  </div>
                </section>
              </div>
            </div>
          )}

          {/* Section 4: Certificate Management */}
          {activeTab === "certificateManagement" && <CertificateManagement />}

          {/* Section 5: Prescription Management */}
          {activeTab === "prescriptionManagement" && <PrescriptionManagement />}

          {/* Section 7: Advice Management */}
          {activeTab === "adviceManagement" && <AdviceManagement />}

          {/* Section 8: Instructions Management */}
          {activeTab === "instructionsManagement" && <InstructionsManagement />}

        </div>
      </div>
      <PrescriptionPreviewModal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        templateId={selectedTemplateForPreview}
        lang={configurations.find(c => c.paramCode === "selectLanguage")?.paramValue?.toLowerCase() || authCtx.hospitalData.prescriptionLanguage}
        hospitalData={{
          ...authCtx.hospitalData,
          PrescHeader: configurations.find(c => c.paramCode.toLowerCase() === "boolprescheader")?.paramValue === "true",
          PrescFooter: configurations.find(c => c.paramCode.toLowerCase() === "boolprescfooter")?.paramValue === "true",
          selectHeader: configurations.find(c => c.paramCode.toLowerCase() === "selectheader")?.paramValue || authCtx.hospitalData.selectHeader,
          prescriptionHeader: configurations.find(c => c.paramCode.toLowerCase() === "prescriptionheader")?.paramValue || authCtx.hospitalData.prescriptionHeader,
          hospitalInstructions: configurations.find(c => c.paramCode.toLowerCase() === "prescriptionfooter")?.paramValue || authCtx.hospitalData.hospitalInstructions,
          applicationThemeColour: configurations.find(c => c.paramCode.toLowerCase() === "applicationthemecolour")?.paramValue || authCtx.hospitalData.applicationThemeColour,
          headerBgColor: configurations.find(c => c.paramCode === "headerBgColor")?.paramValue || authCtx.hospitalData.headerBgColor,
        }}
      />
      <HeaderPreviewModal
        show={showHeaderPreview}
        onHide={() => setShowHeaderPreview(false)}
        hospitalData={{
          ...authCtx.hospitalData,
          selectHeader: selectedHeaderForPreview,
          prescriptionHeader: configurations.find(c => c.paramCode === "prescriptionHeader")?.paramValue || "",
          headerBgColor: configurations.find(c => c.paramCode === "headerBgColor")?.paramValue || "#f0fdfa",
        }}
      />
    </div>
  );
};

export default Config;
